// Колоните на дневника. Работят в СЪРВЪРЕН режим: сортирането, филтрите и
// страницирането се вдигат до `GET /audit-logs`.
//
// Редът отговаря на трите въпроса в реда, в който се задават: КОГА · КАК
// свърши · КАКВО е станало · КЪДЕ (модул, маса, сметка) · КОЙ. Техническото
// (метод, латентност, URL) стои най-вдясно и се гаси от панела „Колони".

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type {
  AuditLogEntry,
  AuditLogFilterOptions,
} from "../../../../api/auditLogs";
import type {
  ChecklistOption,
  ChipCellValue,
  TextCellValue,
} from "../../../../components/dataTable";
import {
  IMPACT_TONE,
  METHOD_TONE,
  OUTCOME_TONE,
  SLOW_MS,
  entityIdOf,
  impactLabelKey,
  moduleLabelKey,
  outcomeLabelKey,
  outcomeOf,
} from "./auditTaxonomy";
import { TraceChip } from "./TraceChip";

const helper = createColumnHelper<AuditLogEntry>();

/** Ид-тата, по които BE-то може да сортира (`sort_by` в router.py). */
export const AUDIT_SORTABLE_IDS: readonly string[] = [
  "timestamp",
  "operation",
  "method",
  "status_code",
  "duration_ms",
  "table_name",
  "bill_id",
  "user_name",
];

const DOT_CLASS: Record<string, string> = {
  ok: "bg-ok-solid",
  warn: "bg-brand-deep",
  danger: "bg-danger",
  info: "bg-info",
  brand: "bg-brand",
  neutral: "bg-ghost",
};

const CODE_CLASS: Record<string, string> = {
  ok: "text-soft",
  warn: "text-brand-deep-ink",
  danger: "text-danger-ink",
  info: "text-info-deep",
  brand: "text-brand-deep-ink",
  neutral: "text-muted",
};

type Options = {
  filterOptions?: AuditLogFilterOptions;
  onTraceBill: (entry: AuditLogEntry) => void;
  onTraceTable: (tableName: string) => void;
};

export const useAuditColumns = ({
  filterOptions,
  onTraceBill,
  onTraceTable,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const operationOptions: ChecklistOption[] = (
      filterOptions?.operations ?? []
    ).map((operation) => ({ value: operation, label: operation }));
    const methodOptions: ChecklistOption[] = (filterOptions?.methods ?? []).map(
      (method) => ({ value: method, label: method }),
    );
    return [
      helper.accessor((row) => row.timestamp, {
        id: "timestamp",
        header: t("auditLog.col.timestamp"),
        size: 108,
        meta: {
          alwaysVisible: true,
          cell: { kind: "dt", withDate: "always" },
          excel: { format: "date" },
        },
      }),

      helper.accessor((row) => row.status_code, {
        id: "status_code",
        header: t("auditLog.col.outcome"),
        size: 112,
        cell: (context) => {
          const row = context.row.original;
          const outcome = outcomeOf(row);
          const tone = OUTCOME_TONE[outcome];
          return (
            <span
              className="flex items-center gap-2"
              title={t(outcomeLabelKey(outcome))}
            >
              <span
                aria-hidden
                className={`size-[9px] shrink-0 rounded-full ${DOT_CLASS[tone]}`}
              />
              <span
                className={`font-pos-mono text-[12px] font-semibold tabular-nums ${CODE_CLASS[tone]}`}
              >
                {row.status_code}
              </span>
              <span className="sr-only">{t(outcomeLabelKey(outcome))}</span>
            </span>
          );
        },
        meta: {
          label: t("auditLog.col.outcome"),
          filter: { kind: "checklist", options: [
            { value: "2xx", label: t("auditLog.statusClass.2xx") },
            { value: "4xx", label: t("auditLog.statusClass.4xx") },
            { value: "5xx", label: t("auditLog.statusClass.5xx") },
          ] },
          excel: { format: "number" },
        },
      }),

      // Вторият ред казва как е свършило действието, а когато е минало добре —
      // ид-то на записа от пътя. Маса и сметка си имат СВОИ колони, затова тук
      // не се повтарят.
      helper.accessor((row): TextCellValue => {
        const outcome = outcomeOf(row);
        if (outcome !== "ok") {
          return { primary: row.operation, secondary: t(outcomeLabelKey(outcome)) };
        }
        const entityId = row.bill_id === null ? entityIdOf(row.path) : null;
        return {
          primary: row.operation,
          secondary: entityId ? `#${entityId}` : null,
        };
      }, {
        id: "operation",
        header: t("auditLog.col.operation"),
        size: 250,
        meta: {
          alwaysVisible: true,
          cell: { kind: "text", lines: 2 },
          filter: { kind: "checklist", options: operationOptions },
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      helper.accessor(
        (row): ChipCellValue => ({
          tone: "neutral",
          label: t(moduleLabelKey(row.module)),
        }),
        {
          id: "module",
          header: t("auditLog.col.module"),
          size: 124,
          enableSorting: false,
          meta: {
            // БЕЗ фуния: модулът се избира от разбивката „По модул" под
            // обзора — там до всеки модул стои и броят му. Две контроли за
            // едно и също измерение биха си противоречили на екрана.
            cell: { kind: "chip" },
            mobileRole: "label",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row): ChipCellValue | null => {
        const tone = IMPACT_TONE[row.impact];
        return tone ? { tone, label: t(impactLabelKey(row.impact)) } : null;
      }, {
        id: "impact",
        header: t("auditLog.col.impact"),
        size: 104,
        enableSorting: false,
        meta: {
          // БЕЗ фуния: „Пари" и „Настройки" се палят от чиповете над списъка
          // и от кутийките в обзора — те са същото състояние.
          cell: { kind: "chip" },
          mobileRole: "label",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.table_name, {
        id: "table_name",
        header: t("auditLog.col.table"),
        size: 96,
        cell: (context) => {
          const name = context.getValue();
          if (!name) return <span className="text-faint">—</span>;
          return (
            <TraceChip
              label={name}
              title={t("auditLog.trace.tableHint", { name })}
              onClick={() => onTraceTable(name)}
            />
          );
        },
        meta: { label: t("auditLog.col.table"), excel: { format: "text" } },
      }),

      helper.accessor((row) => row.bill_id, {
        id: "bill_id",
        header: t("auditLog.col.billId"),
        size: 96,
        cell: (context) => {
          const billId = context.getValue();
          if (billId === null || billId === undefined) {
            return <span className="text-faint">—</span>;
          }
          return (
            <TraceChip
              label={`#${billId}`}
              title={t("auditLog.trace.billHint", { id: billId })}
              onClick={() => onTraceBill(context.row.original)}
            />
          );
        },
        meta: { label: t("auditLog.col.billId"), excel: { format: "number" } },
      }),

      helper.accessor((row): TextCellValue => ({
        primary: row.user_name ?? "—",
        secondary: row.user_id === null ? null : `#${row.user_id}`,
      }), {
        id: "user_name",
        header: t("auditLog.col.user"),
        size: 150,
        meta: {
          // БЕЗ фуния — човекът се избира от разбивката „По потребител".
          cell: { kind: "text", lines: 2 },
          mobileRole: "strong",
          excel: { format: "text" },
        },
      }),

      helper.accessor(
        (row): ChipCellValue => ({
          tone: METHOD_TONE[row.method] ?? "neutral",
          label: row.method,
        }),
        {
          id: "method",
          header: t("auditLog.col.method"),
          size: 92,
          meta: {
            cell: { kind: "chip" },
            filter: { kind: "checklist", options: methodOptions },
            hideBelow: "tablet",
            mobileRole: "hidden",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row) => row.duration_ms, {
        id: "duration_ms",
        header: t("auditLog.col.duration"),
        size: 104,
        cell: (context) => {
          const ms = context.getValue();
          return (
            <span
              className={`font-pos-mono text-[12px] font-semibold tabular-nums ${
                ms > SLOW_MS ? "text-brand-deep-ink" : "text-soft"
              }`}
            >
              {`${ms} ms`}
            </span>
          );
        },
        meta: {
          label: t("auditLog.col.duration"),
          align: "right",
          filter: { kind: "numberRange" },
          mobileRole: "meta",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.path, {
        id: "path",
        header: t("auditLog.col.path"),
        size: 260,
        enableSorting: false,
        cell: (context) => (
          <span
            title={`${context.row.original.method} ${context.getValue()}`}
            className="block max-w-full truncate font-pos-mono text-[11.5px] font-medium text-muted"
          >
            {context.getValue()}
          </span>
        ),
        meta: {
          label: t("auditLog.col.path"),
          hideBelow: "tablet",
          mobileRole: "hidden",
          excel: { format: "text" },
        },
      }),
    ];
  }, [t, filterOptions, onTraceBill, onTraceTable]);
};
